import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Radio,
  Checkbox,
  DatePicker,
  Upload,
  Rate,
  Button,
  Space,
  Typography,
  Tabs,
  Steps,
  Table,
  Divider,
  Spin,
  Alert,
} from 'antd';
import {
  ArrowLeftOutlined,
  SaveOutlined,
  SendOutlined,
  UploadOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';
import jsonLogic from 'json-logic-js';
import LibraryPage from '../../../components/layout/LibraryPage';
import { useGetTemplateQuery } from '../../../store/api/templateApi';
import { toast } from '../../../components/common/Toast';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

// Helper to check if question should be visible based on conditional logic
const shouldShowQuestion = (question, formValues) => {
  if (!question.conditionalLogic) return true;

  try {
    const result = jsonLogic.apply(question.conditionalLogic, formValues);
    return !!result;
  } catch (error) {
    console.error('Error evaluating conditional logic:', error, question.conditionalLogic);
    return true; // Show field by default if evaluation fails
  }
};

// Component to render questions with conditional logic
const ConditionalQuestions = ({ questions, form, renderField }) => {
  // Watch all form values to trigger re-render when they change
  const formValues = Form.useWatch([], form) || {};

  return (
    <>
      {questions?.map((question, qIdx) => {
        const isVisible = shouldShowQuestion(question, formValues);
        if (!isVisible) return null;

        return <div key={qIdx}>{renderField(question)}</div>;
      })}
    </>
  );
};

export default function TemplatePreview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [activeSheet, setActiveSheet] = useState(0);

  const { data: template, isLoading, error } = useGetTemplateQuery(id);

  const handleBackToBuilder = () => {
    navigate(`/library/builder/${id}`);
  };

  const renderField = (question) => {
    const fieldProps = {
      name: question.id,
      label: question.question,
      rules: question.required ? [{ required: true, message: 'This field is required' }] : [],
      help: question.help_text,
    };

    switch (question.field_type) {
      case 'text':
        return (
          <Form.Item {...fieldProps}>
            <Input placeholder="Enter text" disabled={question.read_only} />
          </Form.Item>
        );

      case 'textarea':
        return (
          <Form.Item {...fieldProps}>
            <TextArea rows={4} placeholder="Enter text" disabled={question.read_only} />
          </Form.Item>
        );

      case 'number':
        return (
          <Form.Item {...fieldProps}>
            <InputNumber style={{ width: '100%' }} placeholder="Enter number" disabled={question.read_only} />
          </Form.Item>
        );

      case 'email':
        return (
          <Form.Item {...fieldProps} rules={[...fieldProps.rules, { type: 'email', message: 'Invalid email' }]}>
            <Input type="email" placeholder="Enter email" disabled={question.read_only} />
          </Form.Item>
        );

      case 'phone':
        return (
          <Form.Item {...fieldProps}>
            <Input placeholder="Enter phone number" disabled={question.read_only} />
          </Form.Item>
        );

      case 'url':
        return (
          <Form.Item {...fieldProps} rules={[...fieldProps.rules, { type: 'url', message: 'Invalid URL' }]}>
            <Input placeholder="https://" disabled={question.read_only} />
          </Form.Item>
        );

      case 'select':
        return (
          <Form.Item {...fieldProps}>
            <Select
              placeholder="Select an option"
              options={question.options?.map((opt) => ({
                label: opt.label || opt,
                value: opt.label || opt.value || opt,
              }))}
              disabled={question.read_only}
            />
          </Form.Item>
        );

      case 'multiselect':
        return (
          <Form.Item {...fieldProps}>
            <Select
              mode="multiple"
              placeholder="Select options"
              options={question.options?.map((opt) => ({
                label: opt.label || opt,
                value: opt.label || opt.value || opt,
              }))}
              disabled={question.read_only}
            />
          </Form.Item>
        );

      case 'radio':
        return (
          <Form.Item {...fieldProps}>
            <Radio.Group disabled={question.read_only}>
              <Space direction="vertical">
                {question.options?.map((opt, idx) => (
                  <Radio key={idx} value={opt.label || opt.value || opt}>
                    {opt.label || opt}
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          </Form.Item>
        );

      case 'checkbox':
        return (
          <Form.Item {...fieldProps} valuePropName="checked">
            <Checkbox.Group disabled={question.read_only}>
              <Space direction="vertical">
                {question.options?.map((opt, idx) => (
                  <Checkbox key={idx} value={opt.label || opt.value || opt}>
                    {opt.label || opt}
                  </Checkbox>
                ))}
              </Space>
            </Checkbox.Group>
          </Form.Item>
        );

      case 'date':
        return (
          <Form.Item {...fieldProps}>
            <DatePicker style={{ width: '100%' }} disabled={question.read_only} />
          </Form.Item>
        );

      case 'datetime':
        return (
          <Form.Item {...fieldProps}>
            <DatePicker showTime style={{ width: '100%' }} disabled={question.read_only} />
          </Form.Item>
        );

      case 'file':
        return (
          <Form.Item {...fieldProps} valuePropName="fileList" getValueFromEvent={(e) => e.fileList}>
            <Upload beforeUpload={() => false} disabled={question.read_only}>
              <Button icon={<UploadOutlined />}>Upload File</Button>
            </Upload>
          </Form.Item>
        );

      case 'rating':
        return (
          <Form.Item {...fieldProps}>
            <Rate disabled={question.read_only} />
          </Form.Item>
        );

      case 'currency':
        return (
          <Form.Item {...fieldProps}>
            <InputNumber
              style={{ width: '100%' }}
              prefix="$"
              precision={2}
              placeholder="0.00"
              disabled={question.read_only}
            />
          </Form.Item>
        );

      case 'percentage':
        return (
          <Form.Item {...fieldProps}>
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              max={100}
              formatter={(value) => `${value}%`}
              parser={(value) => value.replace('%', '')}
              disabled={question.read_only}
            />
          </Form.Item>
        );

      default:
        return (
          <Form.Item {...fieldProps}>
            <Input placeholder="Enter value" disabled={question.read_only} />
          </Form.Item>
        );
    }
  };

  if (isLoading) {
    return (
      <LibraryPage title="Loading...">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <Spin size="large" />
        </div>
      </LibraryPage>
    );
  }

  if (error || !template) {
    return (
      <LibraryPage title="Error">
        <Alert
          message="Failed to load template"
          description="The template could not be loaded. Please try again."
          type="error"
          showIcon
        />
        <Button
          style={{ marginTop: 16 }}
          onClick={() => navigate('/library/templates')}
          icon={<ArrowLeftOutlined />}
        >
          Back to Templates
        </Button>
      </LibraryPage>
    );
  }

  const sheets = template.template_data?.sheets || [];
  const totalSteps = sheets.length + 1; // Add 1 for Review & Submit step
  const isReviewStep = activeSheet === sheets.length;

  return (
    <LibraryPage
      title={template.name}
      breadcrumbs={[
        { label: 'Library', path: '/library' },
        { label: 'Templates', path: '/library/templates' },
        { label: template.name },
      ]}
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
        <Card>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {/* Preview Mode Alert */}
            <Alert
              message="Preview Mode"
              description="This is a preview of how the template will appear to users. No data will be saved."
              type="info"
              showIcon
              banner
            />

            {/* Stepper for multi-sheet navigation */}
            <Steps
              current={activeSheet}
              onChange={(current) => setActiveSheet(current)}
              style={{ marginBottom: 24 }}
              items={[
                ...sheets.map((sheet, idx) => ({
                  title: sheet.name,
                  status: activeSheet === idx ? 'process' : activeSheet > idx ? 'finish' : 'wait',
                })),
                {
                  title: 'Review & Submit',
                  status: isReviewStep ? 'process' : activeSheet > sheets.length ? 'finish' : 'wait',
                },
              ]}
            />

            {/* Form */}
            <Form form={form} layout="vertical" size="small">
              {!isReviewStep && sheets.map((sheet, sheetIdx) => (
                <div key={sheetIdx} style={{ display: sheetIdx === activeSheet ? 'block' : 'none' }}>
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    {sheet.sections?.map((section, sectionIdx) => (
                      <Card
                        key={sectionIdx}
                        title={section.name}
                        type="inner"
                        style={{ background: '#F9FAFB' }}
                      >
                        {section.categories?.map((category, catIdx) => (
                          <div
                            key={catIdx}
                            style={{
                              marginBottom: 16,
                              paddingBottom: catIdx < (section.categories?.length || 0) - 1 ? 16 : 0,
                              borderBottom: catIdx < (section.categories?.length || 0) - 1 ? '1px solid #E5E7EB' : 'none'
                            }}
                          >
                            {category.name && (
                              <Title level={5} style={{ marginBottom: 12, fontSize: '14px', fontWeight: 600 }}>
                                {category.name}
                              </Title>
                            )}
                            <ConditionalQuestions
                              questions={category.questions}
                              form={form}
                              renderField={renderField}
                            />
                          </div>
                        ))}
                      </Card>
                    ))}
                  </Space>
                </div>
              ))}

              {/* Review & Submit Screen */}
              {isReviewStep && (
                <div>
                  <Alert
                    message="Review Your Responses (Preview)"
                    description="This is how the review screen will appear to users. They can review all responses before submitting."
                    type="info"
                    showIcon
                    style={{ marginBottom: 24 }}
                  />

                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    {sheets.map((sheet, sheetIdx) => (
                      <Card
                        key={sheetIdx}
                        title={sheets.length > 1 ? sheet.name : null}
                        style={{ background: '#FAFAFA' }}
                      >
                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                          {sheet.sections?.map((section, sectionIdx) => (
                            <Card
                              key={sectionIdx}
                              title={section.name}
                              type="inner"
                              size="small"
                              style={{ background: '#fff' }}
                            >
                              {section.categories?.map((category, catIdx) => (
                                <div
                                  key={catIdx}
                                  style={{
                                    marginBottom: catIdx < (section.categories?.length || 0) - 1 ? 16 : 0,
                                    paddingBottom: catIdx < (section.categories?.length || 0) - 1 ? 16 : 0,
                                    borderBottom: catIdx < (section.categories?.length || 0) - 1 ? '1px solid #E5E7EB' : 'none'
                                  }}
                                >
                                  {category.name && (
                                    <Title level={5} style={{ marginBottom: 12, fontSize: '14px', fontWeight: 600 }}>
                                      {category.name}
                                    </Title>
                                  )}
                                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                    {category.questions?.map((question, qIdx) => {
                                      const value = form.getFieldValue(question.id);
                                      const isVisible = shouldShowQuestion(question, form.getFieldsValue());

                                      if (!isVisible) return null;

                                      // Handle table/grid fields - render as actual table
                                      if (question.field_type === 'table') {
                                        const tableConfig = question.tableConfig || {};

                                        return (
                                          <div key={qIdx} style={{ marginBottom: 16 }}>
                                            <Text strong style={{ display: 'block', marginBottom: 8, fontSize: '13px' }}>
                                              {question.question}
                                              {question.required && <Text type="danger"> *</Text>}
                                            </Text>

                                            {tableConfig.orientation === 'rows' ? (
                                              // Row-based table: 2-column layout
                                              <div style={{ border: '1px solid #E5E7EB', borderRadius: '4px', overflow: 'hidden' }}>
                                                {tableConfig.columns?.map((column, colIdx) => {
                                                  const val = value?.[column.key];
                                                  let displayVal;

                                                  if (Array.isArray(val)) {
                                                    displayVal = val.join(', ');
                                                  } else if (val && typeof val === 'object' && val.format) {
                                                    displayVal = val.format('YYYY-MM-DD HH:mm');
                                                  } else if (val instanceof Date) {
                                                    displayVal = val.toLocaleDateString();
                                                  } else if (val) {
                                                    displayVal = String(val);
                                                  } else {
                                                    displayVal = '—';
                                                  }

                                                  return (
                                                    <div
                                                      key={colIdx}
                                                      style={{
                                                        display: 'flex',
                                                        borderBottom: colIdx < tableConfig.columns.length - 1 ? '1px solid #E5E7EB' : 'none',
                                                      }}
                                                    >
                                                      <div style={{
                                                        width: '200px',
                                                        padding: '8px 12px',
                                                        background: '#FAFAFA',
                                                        borderRight: '1px solid #E5E7EB',
                                                      }}>
                                                        <Text strong style={{ fontSize: '13px' }}>{column.title}</Text>
                                                      </div>
                                                      <div style={{ flex: 1, padding: '8px 12px' }}>
                                                        <Text style={{ fontSize: '13px', color: '#595959' }}>{displayVal}</Text>
                                                      </div>
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            ) : (
                                              // Column-based table: traditional table
                                              <Table
                                                dataSource={Array.isArray(value) ? value : []}
                                                columns={tableConfig.columns?.map(col => ({
                                                  title: col.title,
                                                  dataIndex: col.key,
                                                  key: col.key,
                                                  render: (text) => {
                                                    if (Array.isArray(text)) return text.join(', ');
                                                    if (!text) return '—';
                                                    // Format Date objects
                                                    if (text && typeof text === 'object' && text.format) {
                                                      return text.format('YYYY-MM-DD HH:mm');
                                                    }
                                                    if (text instanceof Date) {
                                                      return text.toLocaleDateString();
                                                    }
                                                    return String(text);
                                                  }
                                                })) || []}
                                                pagination={false}
                                                size="small"
                                                bordered
                                                locale={{ emptyText: 'No data' }}
                                              />
                                            )}
                                          </div>
                                        );
                                      }

                                      // Format value for display (non-table fields)
                                      let displayValue = value;

                                      // Handle regular arrays (multiselect, checkbox, etc)
                                      if (Array.isArray(value)) {
                                        displayValue = value.join(', ');
                                      } else if (value && typeof value === 'object' && value.format) {
                                        displayValue = value.format('YYYY-MM-DD HH:mm');
                                      } else if (value instanceof Date) {
                                        displayValue = value.toLocaleDateString();
                                      } else if (typeof value === 'boolean') {
                                        displayValue = value ? 'Yes' : 'No';
                                      } else if (!value) {
                                        displayValue = '—';
                                      }

                                      return (
                                        <div key={qIdx} style={{ marginBottom: 12 }}>
                                          <Text strong style={{ display: 'block', marginBottom: 4, fontSize: '13px' }}>
                                            {question.question}
                                            {question.required && <Text type="danger"> *</Text>}
                                          </Text>
                                          <Text style={{ fontSize: '13px', color: '#595959' }}>{displayValue}</Text>
                                        </div>
                                      );
                                    })}
                                  </Space>
                                </div>
                              ))}
                            </Card>
                          ))}
                        </Space>
                      </Card>
                    ))}
                  </Space>
                </div>
              )}
            </Form>

            <Divider />

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <Button
                onClick={handleBackToBuilder}
                icon={<ArrowLeftOutlined />}
                size="large"
              >
                Back to Builder
              </Button>

              <Space size="middle">
                <Button
                  onClick={() => setActiveSheet(activeSheet - 1)}
                  icon={<LeftOutlined />}
                  size="large"
                  disabled={activeSheet === 0}
                >
                  Previous
                </Button>

                <Button
                  onClick={() => setActiveSheet(activeSheet + 1)}
                  icon={<RightOutlined />}
                  size="large"
                  disabled={isReviewStep}
                >
                  Next
                </Button>
              </Space>
            </div>
          </Space>
        </Card>
      </div>
    </LibraryPage>
  );
}
